import { informesAxios } from '../../../common/services/api'
import { INFORMES_API_PATHS } from '../../../common/constants/api.js'

/**
 * Obtiene el top de personas denunciantes.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getTopDenunciantes = (contrato, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.TOP_DENUNCIANTES, { params: { contrato }, ...config })

/**
 * Obtiene el top de personas con más juicios.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getTopJudiciales = (contrato, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.TOP_JUDICIALES, { params: { contrato }, ...config })

/**
 * Obtiene la evolución mensual de siniestros por período.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} periodoDesde - Período desde en formato YYYYMM
 * @param {string} periodoHasta - Período hasta en formato YYYYMM
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getTopEvolucionSiniestros = (contrato, periodoDesde, periodoHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.EVOLUCION_SINIESTROS, {
    params: { contrato, periodoDesde, periodoHasta },
    ...config,
  })

/**
 * Obtiene el ranking de denuncias agrupado por tipo de siniestro.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getTopRankingSiniestros = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.RANKING_DENUNCIAS, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene la distribución de siniestros por tipo de accidente.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getGraficosTipoAccidente = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.GRAFICOS_TIPO_ACCIDENTE, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene la distribución de siniestros por gravedad.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getGraficosGravedad = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.GRAFICOS_GRAVEDAD, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene la evolución mensual de denuncias para el período.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getGraficosDenuncias = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.GRAFICOS_DENUNCIAS, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene los indicadores de denuncias.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getIndicadoresDenuncias = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.INDICADORES_DENUNCIAS, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene los indicadores de estado de siniestros.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} fechaDesde - Fecha desde en formato YYYY-MM-DD
 * @param {string} fechaHasta - Fecha hasta en formato YYYY-MM-DD
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getIndicadoresEstado = (contrato, fechaDesde, fechaHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.INDICADORES_ESTADO, {
    params: { contrato, fechaDesde, fechaHasta },
    ...config,
  })

/**
 * Obtiene los indicadores frecuenciales.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} periodoHasta - Período hasta en formato YYYYMM
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getIndicadoresFrecuencia = (contrato, periodoHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.INDICADORES_FRECUENCIA, {
    params: { contrato, periodoHasta },
    ...config,
  })

/**
 * Verifica si existen siniestros para el contrato y período dados.
 * @param {number} contrato - Número de contrato del cliente autenticado
 * @param {string} periodoDesde - Período desde en formato YYYYMM
 * @param {string} periodoHasta - Período hasta en formato YYYYMM
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const checkSiniestro = (contrato, periodoDesde, periodoHasta, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.CHECK_SINIESTRO, {
    params: { contrato, periodoDesde, periodoHasta },
    ...config,
  })

/**
 * Genera un PDF a partir de HTML en el backend
 * - Usa responseType: 'arraybuffer' para evitar corrupción del binario en algunos navegadores/proxies
 * - Envía y espera Content-Type adecuados para PDF
 * @param {string} html - HTML completo del informe
 * @param {object} [config] - Config extra de Axios (se mezcla en la 3ra posición)
 * @returns {Promise<import("axios").AxiosResponse<ArrayBuffer>>}
 */
export const generatePdfFromHtml = (html, config = {}) =>
  informesAxios.post(
    INFORMES_API_PATHS.GENERATE_PDF,
    { html },
    {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
        'Content-Type': 'application/json',
      },
      timeout: 120000,
      ...config,
    }
  )

/**
 * Genera URL de descarga para el reporte INSI_SIN_DATOS.
 * @param {object} params - Parámetros del reporte
 * @param {number} params.contrato - Número de contrato del cliente
 * @param {string} params.periodoDesde - Período desde en formato MM/YYYY
 * @param {string} params.periodoHasta - Período hasta en formato MM/YYYY
 * @param {object} [config] - Configuración adicional de Axios (ej: { signal })
 * @returns {Promise<import("axios").AxiosResponse<{url: string}>>} URL del reporte generado
 */
export const generateReporteInsiSinDatos = (params, config = {}) =>
  informesAxios.get(INFORMES_API_PATHS.INSI_SIN_DATOS, {
    params,
    ...config,
  })
